import { createAction } from '@shared/context/reducerUtils';
import type { HostFloorPlanTable } from 'restaurantAdmin/floorPlans/apiHelpers';

interface MergingTable {
  primaryMergeTableId: string;
  secondaryMergeTableIds: string[];
}

export interface SplitMergeState {
  selectedTables: HostFloorPlanTable[];
  mergedTables: MergingTable[];
  unMergedTableIds: string[];
}
enum SplitMergeTablesActionType {
  SelectTable = 'SelectTable',
  UnselectTable = 'UnselectTable',
  ClearMergeState = 'ClearMergeState',
  StageMerge = 'StageMerge',
  StageUnmerge = 'StageUnmerge',
  UndoMerge = 'UndoMerge',
}

export const selectTable = (table: HostFloorPlanTable) =>
  createAction(SplitMergeTablesActionType.SelectTable, { table });
export const unSelectTable = (tableId: string) =>
  createAction(SplitMergeTablesActionType.UnselectTable, { tableId });
export const clearMerges = () =>
  createAction(SplitMergeTablesActionType.ClearMergeState, null);
export const stageMerge = () =>
  createAction(SplitMergeTablesActionType.StageMerge, null);
export const stageUnMerge = () =>
  createAction(SplitMergeTablesActionType.StageUnmerge, null);
export const undoMerge = () =>
  createAction(SplitMergeTablesActionType.UndoMerge, null);

export const splitMergeTablesInitialState: SplitMergeState = {
  selectedTables: [],
  mergedTables: [],
  unMergedTableIds: [],
};

export const splitMergeTablesReducer = (
  state: SplitMergeState,
  action:
    | ReturnType<typeof selectTable>
    | ReturnType<typeof unSelectTable>
    | ReturnType<typeof clearMerges>
    | ReturnType<typeof stageMerge>
    | ReturnType<typeof stageUnMerge>
    | ReturnType<typeof undoMerge>,
): SplitMergeState => {
  switch (action.type) {
    case SplitMergeTablesActionType.SelectTable:
      return {
        ...state,
        selectedTables: [...state.selectedTables, action.payload.table],
      };
    case SplitMergeTablesActionType.UnselectTable:
      return {
        ...state,
        selectedTables: state.selectedTables.filter(
          (table) => table.id !== action.payload.tableId,
        ),
      };
    case SplitMergeTablesActionType.ClearMergeState:
      return splitMergeTablesInitialState;
    case SplitMergeTablesActionType.StageMerge: {
      const [primarySelectedTable, ...secondarySelectedTables] =
        state.selectedTables;
      const secondarySelectedTableIds = secondarySelectedTables.map(
        (table) => table.id,
      );
      const secondariesThatAreMerged = state.mergedTables.filter(
        (alreadyMergedTable) =>
          secondarySelectedTableIds.includes(
            alreadyMergedTable.primaryMergeTableId,
          ),
      );
      const secondariesSecondaryIds = secondariesThatAreMerged.flatMap(
        (table) => table.secondaryMergeTableIds,
      );
      const mergedTablesFilteredOfOldPrimaries = state.mergedTables.filter(
        (table) =>
          !secondarySelectedTableIds.includes(table.primaryMergeTableId),
      );

      return {
        ...state,
        selectedTables: [],
        mergedTables: [
          ...mergedTablesFilteredOfOldPrimaries,
          {
            primaryMergeTableId: primarySelectedTable.id,
            secondaryMergeTableIds: [
              ...secondarySelectedTableIds,
              ...secondariesSecondaryIds,
            ],
          },
        ],
      };
    }
    case SplitMergeTablesActionType.StageUnmerge:
      return {
        ...state,
        selectedTables: [],
        unMergedTableIds: [
          ...state.unMergedTableIds,
          ...state.selectedTables.map((table) => table.id),
        ],
      };
    case SplitMergeTablesActionType.UndoMerge:
      return {
        ...state,
        selectedTables: [],
        mergedTables: state.mergedTables.filter(
          (mt) => mt.primaryMergeTableId !== state.selectedTables[0].id,
        ),
      };
    default:
      return state;
  }
};
